import React from 'react';
import { Event } from '../../types/events.types';
import styles from './upcoming_event.module.scss';
import { EVENTS_PATH } from '../../../utilities/devex_paths';
import DateRange from '../date_range/date_range';
const CLOUDINARY_BASE_PATH = 'https://res.cloudinary.com/devex/image';
const FALLBACK_IMAGE = 'devex-generic-cccdb08fd0bb6bcf8a8a2d3b6c21a76b.png';
const rowMaxHeight = 188;

const EVENT_PREVIEW_IMAGE_PATH_FOR_XS_AND_DESKTOP = `
  ${CLOUDINARY_BASE_PATH}/fetch/c_fill,q_auto,f_auto,h_${rowMaxHeight},w_272/
`;

const FALLBACK_EVENT_IMAGE_URL_FOR_XS_AND_DESKTOP = `
  ${CLOUDINARY_BASE_PATH}/asset/c_fill,q_auto,f_auto,h_${rowMaxHeight},w_272/${FALLBACK_IMAGE}
`;

const EVENT_PREVIEW_IMAGE_PATH_FOR_SM = `
  ${CLOUDINARY_BASE_PATH}/fetch/c_fill,q_auto,f_auto,h_${rowMaxHeight},w_520/
`;

const FALLBACK_EVENT_SRC_URL_FOR_SM = `
  ${CLOUDINARY_BASE_PATH}/asset/c_fill,q_auto,f_auto,h_${rowMaxHeight},w_520/${FALLBACK_IMAGE}
`;

interface Props {
  event: Event;
  plausibleSection: string;
}

const UpcomingEvent: React.FC<Props> = ({ event, plausibleSection }) => {
  const EVENT_PATH = `${EVENTS_PATH}/${event.slug_and_id}`;

  // Decide which image to use for the event thumbnail.
  // Use thumbnail file if available; otherwise, use preview_image.
  const thumbnailUrl = (event?.thumbnail && event.thumbnail?.public_url) || event?.preview_image;
  // If neither is available, use the fallback image URL.
  const eventImageUrlForMobile = thumbnailUrl
    ? EVENT_PREVIEW_IMAGE_PATH_FOR_SM + thumbnailUrl
    : FALLBACK_EVENT_SRC_URL_FOR_SM;

  const eventImageUrlForDesktop = thumbnailUrl
    ? EVENT_PREVIEW_IMAGE_PATH_FOR_XS_AND_DESKTOP + thumbnailUrl
    : FALLBACK_EVENT_IMAGE_URL_FOR_XS_AND_DESKTOP;

  const renderImageForDesktop = (
    <div className='d-none d-md-block'>
      <img src={eventImageUrlForDesktop} alt={event.title} className='img-fluid' loading='lazy' />
    </div>
  );

  const renderImageForMobile = (
    <div className='d-block d-md-none'>
      <img src={eventImageUrlForMobile} alt={event.title} className='card-img-top' loading='lazy' />
    </div>
  );

  const plausibleEventClass = `
    plausible-event-name=click
    plausible-event-intention=view-event-details
    plausible-event-section=${plausibleSection}-upcoming-events`;

  return (
    <div className='row'>
      <div className='card border-0'>
        <a
          href={event.external_registration_url || EVENT_PATH}
          className={`stretched-link ${plausibleEventClass}`}
          target={event.external_registration_url ? '_blank' : undefined}
          rel={event.external_registration_url ? 'noopener noreferrer' : undefined}
        >
          <div className={`row g-0 ${styles.upcomingEvent}`}>
            <div className='col-md-4 col-xxl-3'>
              {renderImageForMobile}
              {renderImageForDesktop}
            </div>
            <div className='col-md-8 col-xxl-9'>
              <div className='card-body pt-lg-45 me-xxl-7'>
                <h3 className='card-title mb-0 lh-sm text-gray-dark'>
                  {event.title}
                </h3>
                <p className='card-text fs-6 fst-italic devex-font-family-serif'>
                  <DateRange event={event} />
                </p>
                <div className='d-block d-md-none'>
                  <p className='card-text text-gray-li stretched-link'>{event?.preview_text}</p>
                </div>
                <div className='d-none d-md-block'>
                  <p className='card-text text-gray-li stretched-link text-truncate'>
                    {event?.preview_text}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </a>
      </div>
    </div>
  );
};

export default UpcomingEvent;
